import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const InsightDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://zvpxmxcznimhwhxjnhvn.supabase.co';
        const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || 'YOUR_PUBLIC_ANON_KEY';
        const WORKSPACE_ID = import.meta.env.VITE_WORKSPACE_ID || '1443c1b6-cab0-4cdd-867b-38987695815c';

        // Assuming you need to fetch by slug directly from supabase, although you previously just fetched all posts.
        const response = await fetch(`${SUPABASE_URL}/rest/v1/blog_posts?workspace_id=eq.${WORKSPACE_ID}&slug=eq.${slug}&status=eq.published`, {
          headers: {
            'apikey': ANON_KEY,
            'Authorization': `Bearer ${ANON_KEY}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch blog post');
        }

        const data = await response.json();
        if (data.length === 0) {
          throw new Error('Post not found');
        }

        setPost(data[0]);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-24 min-h-screen bg-slate-50 dark:bg-brand-dark transition-colors duration-300 flex items-center justify-center">
        <p className="text-slate-600 dark:text-slate-400 text-lg">Loading insight...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="pt-24 min-h-screen bg-slate-50 dark:bg-brand-dark transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
          <div className="bg-red-50 dark:bg-red-900/20 text-brand-red dark:text-red-400 p-6 rounded-xl text-center border border-red-200 dark:border-red-800/30">
            <p>Error loading post: {error || 'Post not found'}</p>
          </div>
          <div className="mt-8 text-center">
            <Link to="/insights" className="inline-flex items-center gap-2 text-brand-red dark:text-brand-cyan hover:underline">
              <SafeIcon name="ArrowLeft" /> Back to Insights
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 min-h-screen bg-slate-50 dark:bg-brand-dark transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8">

        <div className="mb-8">
          <Link to="/insights" className="inline-flex items-center gap-2 text-brand-red dark:text-brand-cyan hover:underline font-medium">
            <SafeIcon name="ArrowLeft" /> Back to Insights
          </Link>
        </div>

        <article className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          {post.image && (
            <div className="w-full h-64 sm:h-80 md:h-96 overflow-hidden">
              <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          )}

          <div className="p-6 sm:p-8 md:p-12">
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400 mb-4">
                <time dateTime={post.published_at}>
                  {new Date(post.published_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </time>
                {post.author && (
                  <>
                    <span className="hidden sm:inline">•</span>
                    <span>By {post.author}</span>
                  </>
                )}
                {post.category && (
                  <>
                    <span className="hidden sm:inline">•</span>
                    <span className="bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-md text-slate-700 dark:text-slate-300">
                      {post.category}
                    </span>
                  </>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-brand-dark dark:text-white leading-tight mb-6">
                {post.title}
              </h1>
            </header>

            <div
              className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-bold prose-a:text-brand-red dark:prose-a:text-brand-cyan hover:prose-a:text-brand-dark dark:hover:prose-a:text-white prose-img:rounded-xl"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>
        </article>
      </div>
    </div>
  );
};

export default InsightDetail;
